import { logger } from '@adornis/base/logging.js';
import { loadRawEntityDataByIDOrFail } from '@adornis/bh-util/util.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { format } from 'date-fns';
import { getMailTemplateForInstitution } from '../api/mail-templates.js';
import { InstitutionMailTemplate, MailTemplateType, MailTemplateVars } from '../entities/mail-templates.js';
import { ParticipationWorkshop } from '../entities/ParticipationWorkshop.js';
import { User } from '../entities/user.js';
import { Workshop } from '../entities/workshop.js';
import { assembleInstitutionMail } from '../mails/assemble-mail.js';
import { institutionMailHint, institutionMailVariables } from '../mails/mail-template-catalog.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { isDefined } from '../util.js';
import { getNotificationType } from './_helper.js';

defineMailTemplate(MailTemplateType.NICHT_ERSCHIENEN, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Nicht erschienen',
    content: `<p>Hallo {{firstname}} {{lastname}},</p>
<p>du bist leider trotz mehrerer Erinnerungen nicht zur Veranstaltung {{product-name}} am {{event-dates}} erschienen und hast dich vorher nicht abgemeldet. Das ist schade – insbesondere, weil andere Studierende auf der Warteliste standen und gerne teilgenommen h&auml;tten.</p>
<p>Bitte beachte: Wenn dies noch einmal ohne Abmeldung passiert, wirst du f&uuml;r den Rest des Semesters von weiteren Veranstaltungen ausgeschlossen. Das m&ouml;chten wir nat&uuml;rlich vermeiden, daher gib uns k&uuml;nftig kurz Bescheid, falls du nicht teilnehmen kannst.</p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>------------------------------------------------------------------</p>
<p>Hello {{firstname}} {{lastname}},</p>
<p>you did not attend the event {{product-name}} on {{event-dates}} despite several reminders and without cancelling beforehand. This is unfortunate, especially since other students were on the waiting list and could not get a spot.</p>
<p>Please note: If this happens again without prior cancellation, you will be excluded from all further sessions for the rest of the semester. We would like to avoid this, so please make sure to contact us in advance if you cannot attend.</p>
<p>Kind regards</p>`,
    footer: '',
  },
  variables: institutionMailVariables(MailTemplateType.NICHT_ERSCHIENEN),
  attachments: [],
  displayName: 'Nicht erschienen',
  editorHint: () => institutionMailHint(MailTemplateType.NICHT_ERSCHIENEN),
});

export async function notifyNichtErschienen(participationID: string) {
  const participation = await loadRawEntityDataByIDOrFail(ParticipationWorkshop, participationID, {
    _id: 1,
    _class: 1,
    userID: 1,
    productID: 1,
  });

  const product = await loadRawEntityDataByIDOrFail(Workshop, participation.productID, {
    _id: 1,
    name: 1,
    institutionID: 1,
    appointments: { _id: 1, start: 1, end: 1 },
  });

  const user = await loadRawEntityDataByIDOrFail(User, participation.userID, {
    _id: 1,
    _class: 1,
    firstName: 1,
    lastName: 1,
    email: 1,
    whatsappNotification: 1,
    whatsappPhone: 1,
  });

  const institutionID = product.institutionID;
  const mailTemplate = await getMailTemplateForInstitution(
    institutionID,
    MailTemplateType.NICHT_ERSCHIENEN,
  )(InstitutionMailTemplate.allFields);

  const dates = product.appointments
    ?.map(a => a.start)
    .filter(isDefined)
    ?.map(start => format(start, 'dd.MM.yyyy'))
    .join(', ');

  const notificationType = await getNotificationType(user, mailTemplate, institutionID);
  if (notificationType === 'email') {
    const mailVars = {
      [MailTemplateVars.FIRST_NAME]: InstitutionMailTemplate.mapFirstNameVar(user),
      [MailTemplateVars.LAST_NAME]: InstitutionMailTemplate.mapLastNameVar(user),
      [MailTemplateVars.PRODUCT_NAME]: product.name ?? '',
      [MailTemplateVars.EVENT_DATES]: dates ?? '',
    };

    const { subject, content } = (await assembleInstitutionMail({
      template: mailTemplate,
      infos: mailVars as any,
    })) as { subject: string; content: any };

    if (!content) throw new Error('mail template has no content!');
    if (!subject) throw new Error('mail template has no subject!');

    await sendInstitutionEmail({
      email: user.email,
      institutionID,
      content,
      subject,
    });
  } else {
    logger.warn(
      { notificationType, notification: mailTemplate.emailType },
      'no notification send because email notification is not activated',
    );
  }
}
